import { createContextApi, CACHE_POLICY, providesOne, providesList, createCursorPagedEndpoint, invalidatesOne, registerCrossContextInvalidation } from '@quickbee/store';

// src/index.ts
var DISPUTE_TAGS = ["Dispute", "DisputeTimeline"];
function invalidatesDispute(id) {
  return [...invalidatesOne("Dispute", id), { type: "DisputeTimeline", id }];
}
var disputeEndpoints = (build) => ({
  /**
   * `GET /disputes` — the ops queue. `disputes:read:tenant`.
   *
   * Note the parameter is `pageSize`, not the platform's usual `size`: this
   * controller binds `@RequestParam int pageSize` directly rather than going
   * through `PageRequestArgumentResolver`, and it exposes no `sort`.
   */
  listDisputes: build.query({
    query: (params) => ({ url: "/disputes", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Dispute", result),
    // Only the retention half of CACHE_POLICY.transactionalList. Its
    // `refetchOnMountOrArgChange` is wrong for an *accumulating* endpoint: the
    // remount refetch reuses the entry's last args — cursor included — and the
    // cursor helper's `merge` appends whenever `arg.cursor` is set, so every
    // mount past page one would duplicate the rows already on screen.
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  /**
   * The same read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listDisputes} drops `cursor` from its cache key and appends every response into a
   * single entry, which is right for a "load more" list and wrong for a numbered pager: paging
   * forward would grow the rendered set rather than replace it, and paging *back* would re-fetch
   * a walked cursor and append its rows a second time. This one keeps the cursor in the cache
   * key, so each page is its own entry and Previous is answered from cache.
   *
   * Same pair, same reason, as `api-audit`'s `listEntries`/`listEntriesPage`.
   */
  listDisputesPage: build.query({
    query: (params) => ({ url: "/disputes", params }),
    providesTags: (result) => providesList("Dispute", result),
    // The full transactional-list policy, unlike the accumulating twin: a discrete page may
    // safely refetch on mount, because a page replaces rather than appends.
    ...CACHE_POLICY.transactionalList
  }),
  /**
   * `GET /disputes/mine` — the customer's own disputes. `disputes:read:own`.
   *
   * Returns a plain array: the endpoint is `ApiResponse<List<…>>`, not
   * `PagedApiResponse`, so there is no cursor to accumulate and therefore no
   * `createCursorPagedEndpoint` here.
   */
  listMyDisputes: build.query({
    query: (args) => ({ url: "/disputes/mine", params: args ?? {} }),
    providesTags: (result) => providesList("Dispute", result),
    ...CACHE_POLICY.transactionalList
  }),
  /** `GET /disputes/{id}`. `disputes:read:own` + `DisputeOwnershipPolicy`. */
  getDispute: build.query({
    query: (id) => ({ url: `/disputes/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Dispute", id),
    ...CACHE_POLICY.transactionalList
  }),
  /** `GET /disputes/{id}/timeline` — oldest-first activity log. */
  getDisputeTimeline: build.query({
    query: (id) => ({ url: `/disputes/${id}/timeline` }),
    providesTags: (_r, _e, id) => [{ type: "DisputeTimeline", id }],
    ...CACHE_POLICY.transactionalList
  }),
  /**
   * `POST /disputes` — the portfolio "Raise a Dispute" widget. `disputes:create:own`.
   *
   * `draftId` is the caller's stable handle on *this* submission and never
   * reaches the wire; it exists only to key idempotency. The server also rejects
   * a second open dispute on the same entry (`DuplicateOpenDisputeException`),
   * but that fires on a genuinely new request — it is not a substitute for a key
   * that makes a double-tap replay the first attempt.
   */
  raiseDispute: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/disputes",
      method: "POST",
      body,
      idempotencyKey: `dispute-raise:${draftId}`
    }),
    invalidatesTags: [{ type: "Dispute", id: "LIST" }]
  }),
  /** `POST /disputes/{id}/start-review` — `OPEN → UNDER_REVIEW`. `disputes:start-review:tenant`. */
  startReview: build.mutation({
    query: ({ id }) => ({
      url: `/disputes/${id}/start-review`,
      method: "POST",
      idempotencyKey: `dispute-start-review:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesDispute(id)
  }),
  /**
   * `POST /disputes/{id}/reassign`. `disputes:reassign:tenant`.
   *
   * The key includes the target, so reassigning to a *different* handler is a
   * new request while a double-submit of the same one is a replay.
   */
  reassignDispute: build.mutation({
    query: ({ id, newAssigneeStaffId }) => ({
      url: `/disputes/${id}/reassign`,
      method: "POST",
      body: { newAssigneeStaffId },
      idempotencyKey: `dispute-reassign:${id}:${newAssigneeStaffId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesDispute(id)
  }),
  /** `POST /disputes/{id}/resolve`. `disputes:resolve:tenant`. Requires a note (INV-7). */
  resolveDispute: build.mutation({
    query: ({ id, resolutionNote }) => ({
      url: `/disputes/${id}/resolve`,
      method: "POST",
      body: { resolutionNote },
      idempotencyKey: `dispute-resolve:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesDispute(id)
  }),
  /** `POST /disputes/{id}/reject`. `disputes:reject:tenant`. Same body as resolve. */
  rejectDispute: build.mutation({
    query: ({ id, resolutionNote }) => ({
      url: `/disputes/${id}/reject`,
      method: "POST",
      body: { resolutionNote },
      idempotencyKey: `dispute-reject:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesDispute(id)
  }),
  /** `POST /disputes/{id}/escalate`. `disputes:escalate:tenant`. */
  escalateDispute: build.mutation({
    query: ({ id, reason }) => ({
      url: `/disputes/${id}/escalate`,
      method: "POST",
      body: { reason },
      idempotencyKey: `dispute-escalate:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesDispute(id)
  }),
  /**
   * `POST /disputes/{id}/close` — archives a decided dispute. `disputes:close:tenant`.
   *
   * Takes **no body**: the controller declares no `@RequestBody`, so a `note`
   * sent here would be silently discarded rather than recorded.
   */
  closeDispute: build.mutation({
    query: ({ id }) => ({
      url: `/disputes/${id}/close`,
      method: "POST",
      idempotencyKey: `dispute-close:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesDispute(id)
  })
});

// src/lifecycle.ts
var DISPUTE_STATUSES = [
  "OPEN",
  "UNDER_REVIEW",
  "ESCALATED",
  "RESOLVED",
  "REJECTED",
  "CLOSED"
];
var DISPUTE_CATEGORIES = [
  "INCORRECT_GOLD_RATE",
  "WRONG_AMOUNT_CREDITED",
  "DUPLICATE_ENTRY",
  "MISSING_INSTALMENT",
  "REDEMPTION_NOT_PROCESSED",
  "BONUS_NOT_APPLIED",
  "INCORRECT_BALANCE",
  "OTHER"
];
var ACTIONABLE = ["OPEN", "UNDER_REVIEW", "ESCALATED"];
function isActionable(status) {
  return ACTIONABLE.includes(status);
}
function isDecided(status) {
  return status === "RESOLVED" || status === "REJECTED";
}
function canStartReview(status) {
  return status === "OPEN";
}
function canReassign(status) {
  return status !== "CLOSED";
}
function canEscalate(status) {
  return isActionable(status) && status !== "ESCALATED";
}
function canResolve(status) {
  return isActionable(status);
}
function canReject(status) {
  return isActionable(status);
}
function canClose(status) {
  return isDecided(status);
}

// src/index.ts
var disputeApi = createContextApi({
  context: "dispute",
  reducerPath: "disputeApi",
  tagTypes: DISPUTE_TAGS,
  endpoints: disputeEndpoints
});
var DISPUTE_INVALIDATION_RULES = [
  {
    event: "dispute.changed",
    invalidate: ({ disputeId }) => disputeId ? [
      { type: "Dispute", id: disputeId },
      { type: "Dispute", id: "LIST" },
      { type: "DisputeTimeline", id: disputeId }
    ] : [],
    settleDelayMs: 0
  }
];
function registerDisputeInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: disputeApi,
    rules: DISPUTE_INVALIDATION_RULES
  });
}
var {
  useListDisputesQuery,
  useListDisputesPageQuery,
  useListMyDisputesQuery,
  useGetDisputeQuery,
  useGetDisputeTimelineQuery,
  useRaiseDisputeMutation,
  useStartReviewMutation,
  useReassignDisputeMutation,
  useResolveDisputeMutation,
  useRejectDisputeMutation,
  useEscalateDisputeMutation,
  useCloseDisputeMutation
} = disputeApi;

export { DISPUTE_CATEGORIES, DISPUTE_INVALIDATION_RULES, DISPUTE_STATUSES, DISPUTE_TAGS, canClose, canEscalate, canReassign, canReject, canResolve, canStartReview, disputeApi, isActionable, isDecided, registerDisputeInvalidation, useCloseDisputeMutation, useEscalateDisputeMutation, useGetDisputeQuery, useGetDisputeTimelineQuery, useListDisputesPageQuery, useListDisputesQuery, useListMyDisputesQuery, useRaiseDisputeMutation, useReassignDisputeMutation, useRejectDisputeMutation, useResolveDisputeMutation, useStartReviewMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map